<div class="customer-portal-access-page p-fluid">
  <app-page-shell>
    <p-toast></p-toast>
    <p-confirmDialog [style]="{ width: '450px' }"></p-confirmDialog>

    <app-page-hero
      eyebrow="CRM Access"
      title="Customer Portal Access"
      subtitle="Grant tenant-managed visibility to existing contacts, scoped to the right CRM client and account.">
      <div actions>
        <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="loadReferenceData()"></p-button>
        <p-button label="Grant Access" icon="pi pi-plus" (click)="openGrantAccess()"></p-button>
      </div>
    </app-page-hero>

    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    <app-page-search-panel
      title="Filter Portal Access"
      [activeFilters]="activeSearchCount"
      [collapsed]="true"
      (clear)="clearSearch()"
      (search)="applySearch()">
      <div class="search-grid">
        <div class="field">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <input
                id="portalAccessSearch"
                pInputText
                [value]="searchText()"
                (input)="searchText.set($any($event.target).value)"
                (keydown.enter)="applySearch()">
              <label for="portalAccessSearch">Search contact, email, client, role</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>

        <div class="field">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select
                inputId="portalAccessClientFilter"
                [options]="clientOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="crmClientFilter()"
                (ngModelChange)="crmClientFilter.set($event)"
                [showClear]="true"
                [filter]="true"
                styleClass="w-full">
              </p-select>
              <label for="portalAccessClientFilter">CRM Client</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>

        <div class="field">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select
                inputId="portalAccessStatusFilter"
                [options]="statusOptions"
                optionLabel="label"
                optionValue="value"
                [ngModel]="statusFilter()"
                (ngModelChange)="statusFilter.set($event)"
                [showClear]="true"
                styleClass="w-full">
              </p-select>
              <label for="portalAccessStatusFilter">Status</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
      </div>
    </app-page-search-panel>

    <app-page-data-panel
      title="Portal Directory"
      [subtitle]="lastUpdatedText"
      [resultText]="filteredAccesses().length + ' customer portal access rows'">
      <p-table
        [value]="filteredAccesses()"
        [rows]="10"
        [paginator]="true"
        [loading]="loading()"
        [rowHover]="true"
        dataKey="accessId"
        styleClass="p-datatable-sm p-datatable-gridlines portal-access-table"
        [tableStyle]="{ 'min-width': '74rem' }"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} access rows"
        [showCurrentPageReport]="true">
        <ng-template pTemplate="header">
          <tr>
            <th>Contact</th>
            <th>CRM Scope</th>
            <th>Status</th>
            <th>Portal Roles</th>
            <th>Lifecycle</th>
            <th class="text-center" style="width: 13rem">Actions</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-access>
          <tr>
            <td>
              <div class="stack-cell">
                <span class="cell-title">{{ access.customerName || 'Unknown contact' }}</span>
                <div class="cell-meta">
                  <span><i class="pi pi-envelope"></i>{{ access.emailAddress || 'Email n/a' }}</span>
                  <span><i class="pi pi-hashtag"></i>ID {{ access.customerId }}</span>
                </div>
              </div>
            </td>
            <td>
              <div class="stack-cell">
                <span class="cell-title">{{ access.crmClientName || 'CRM client missing' }}</span>
                <div class="cell-meta">
                  <span><i class="pi pi-building"></i>Client #{{ access.crmClientId }}</span>
                  <span><i class="pi pi-sitemap"></i>{{ access.crmAccountName || 'All client accounts' }}</span>
                </div>
              </div>
            </td>
            <td>
              <div class="stack-cell">
                <p-tag [value]="access.status" [severity]="statusSeverity(access.status)"></p-tag>
                <div class="cell-meta">
                  <span><i class="pi pi-clock"></i>{{ access.updatedAt ? (access.updatedAt | date:'medium') : 'No recent update' }}</span>
                </div>
              </div>
            </td>
            <td>
              <div class="role-chip-list" *ngIf="access.roleNames?.length; else noRoleAssigned">
                <p-chip *ngFor="let roleName of access.roleNames" [label]="roleName"></p-chip>
              </div>
              <ng-template #noRoleAssigned>
                <span class="muted-copy">No portal role assigned</span>
              </ng-template>
            </td>
            <td>
              <div class="stack-cell">
                <span class="cell-title">{{ lifecycleLabel(access) }}</span>
                <div class="cell-meta">
                  <span><i class="pi pi-calendar"></i>{{ primaryLifecycleDate(access) ? (primaryLifecycleDate(access) | date:'medium') : 'Pending' }}</span>
                </div>
              </div>
            </td>
            <td class="text-center">
              <div class="row-actions">
                <button
                  pButton
                  pRipple
                  icon="pi pi-users"
                  class="p-button-rounded p-button-text p-button-secondary"
                  (click)="openRoleDialog(access)"
                  pTooltip="Change role">
                </button>
                <button
                  pButton
                  pRipple
                  icon="pi pi-check-circle"
                  class="p-button-rounded p-button-text p-button-success"
                  [disabled]="access.status === 'ACTIVE'"
                  (click)="confirmStatusChange(access, 'ACTIVE')"
                  pTooltip="Activate">
                </button>
                <button
                  pButton
                  pRipple
                  icon="pi pi-pause-circle"
                  class="p-button-rounded p-button-text p-button-warning"
                  [disabled]="access.status === 'SUSPENDED'"
                  (click)="confirmStatusChange(access, 'SUSPENDED')"
                  pTooltip="Suspend">
                </button>
                <button
                  pButton
                  pRipple
                  icon="pi pi-ban"
                  class="p-button-rounded p-button-text p-button-danger"
                  [disabled]="access.status === 'REVOKED'"
                  (click)="confirmStatusChange(access, 'REVOKED')"
                  pTooltip="Revoke">
                </button>
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="6">
              <div class="empty-state">
                <i class="pi pi-users"></i>
                <strong>No customer portal access rows found</strong>
                <span>Grant access to an existing contact and map the right CRM scope.</span>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </app-page-data-panel>
  </app-page-shell>
</div>

<p-dialog
  [visible]="accessDialog()"
  (visibleChange)="accessDialog.set($event)"
  (onHide)="hideAccessDialog()"
  [style]="{ width: '1080px', maxWidth: '96vw' }"
  [breakpoints]="{ '1100px': '94vw', '900px': '96vw' }"
  header="Grant Customer Portal Access"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="p-fluid portal-access-dialog">
  <ng-template pTemplate="content">
    <form [formGroup]="accessForm" class="dialog-shell">
      <aside class="dialog-summary">
        <div class="summary-icon"><i class="pi pi-shield"></i></div>
        <span class="summary-kicker">Tenant-managed portal access</span>
        <h3>{{ selectedInviteRoleName() }}</h3>
        <p>{{ selectedInviteRoleDescription() }}</p>

        <div class="summary-facts">
          <div><span>Scope</span><strong>{{ dialogScopeSummary() }}</strong></div>
          <div><span>Status</span><strong>{{ accessForm.get('status')?.value || 'INVITED' }}</strong></div>
          <div><span>Permissions</span><strong>{{ rolePermissionCount(selectedInviteRole()) }}</strong></div>
        </div>
      </aside>

      <section class="dialog-form-panel">
        <div class="dialog-section">
          <div class="dialog-section-title">
            <i class="pi pi-user"></i>
            <span>Contact & Scope</span>
          </div>

          <div class="dialog-grid two">
            <div class="dialog-field span-2">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-users"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    inputId="portalCustomerId"
                    formControlName="customerId"
                    [options]="customerOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [filter]="true"
                    appendTo="body"
                    placeholder="Select existing contact"
                    styleClass="w-full">
                  </p-select>
                  <label for="portalCustomerId">Customer Contact</label>
                </p-iftaLabel>
              </p-inputGroup>
              <small class="p-error" *ngIf="submitted() && accessForm.get('customerId')?.invalid">Customer contact is required.</small>
            </div>

            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    inputId="portalCrmClientId"
                    formControlName="crmClientId"
                    [options]="clientOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [filter]="true"
                    appendTo="body"
                    placeholder="Select CRM client"
                    styleClass="w-full">
                  </p-select>
                  <label for="portalCrmClientId">CRM Client</label>
                </p-iftaLabel>
              </p-inputGroup>
              <small class="p-error" *ngIf="submitted() && accessForm.get('crmClientId')?.invalid">CRM client is required.</small>
            </div>

            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    inputId="portalCrmAccountId"
                    formControlName="crmAccountId"
                    [options]="accessAccountOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [filter]="true"
                    [showClear]="true"
                    appendTo="body"
                    placeholder="Optional CRM account"
                    styleClass="w-full">
                  </p-select>
                  <label for="portalCrmAccountId">CRM Account Optional</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>
          </div>
        </div>

        <div class="dialog-section">
          <div class="dialog-section-title">
            <i class="pi pi-key"></i>
            <span>Access Role</span>
          </div>

          <div class="dialog-grid two">
            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    inputId="portalStatus"
                    formControlName="status"
                    [options]="statusOptions"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    styleClass="w-full">
                  </p-select>
                  <label for="portalStatus">Initial Status</label>
                </p-iftaLabel>
              </p-inputGroup>
            </div>

            <div class="dialog-field">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-id-card"></i></p-inputGroupAddon>
                <p-iftaLabel>
                  <p-select
                    inputId="portalRoleId"
                    formControlName="roleId"
                    [options]="roleOptions()"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                    styleClass="w-full">
                  </p-select>
                  <label for="portalRoleId">Portal Role</label>
                </p-iftaLabel>
              </p-inputGroup>
              <small class="p-error" *ngIf="submitted() && accessForm.get('roleId')?.invalid">Portal role is required.</small>
            </div>
          </div>

          <div class="invite-role-preview" *ngIf="selectedInviteRole() as inviteRole">
            <div class="preview-header">
              <div>
                <span class="summary-kicker">Selected role</span>
                <h4>{{ inviteRole.roleName }}</h4>
                <p>{{ inviteRole.description || 'Portal-safe role' }}</p>
              </div>
              <span class="permission-pill">{{ rolePermissionCount(inviteRole) }} permissions</span>
            </div>

            <div class="permission-chip-grid">
              <span class="permission-chip" *ngFor="let permission of inviteRole.permissions">{{ permission.permissionName }}</span>
            </div>
          </div>
        </div>
      </section>
    </form>
  </ng-template>

  <ng-template pTemplate="footer">
    <span class="dialog-footer-spacer"></span>
    <p-button label="Cancel" icon="pi pi-times" severity="secondary" [text]="true" (click)="hideAccessDialog()"></p-button>
    <p-button label="Save Access" icon="pi pi-check" (click)="saveAccess()" [loading]="saving()" [disabled]="saving()"></p-button>
  </ng-template>
</p-dialog>

<p-dialog
  [visible]="roleDialog()"
  (visibleChange)="roleDialog.set($event)"
  (onHide)="hideRoleDialog()"
  [style]="{ width: '1120px', maxWidth: '96vw' }"
  [breakpoints]="{ '1100px': '94vw', '900px': '96vw' }"
  header="Change Portal Role"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="p-fluid portal-role-dialog">
  <ng-template pTemplate="content">
    <div class="role-manager">
      <aside class="role-picker">
        <div class="picker-header">
          <div>
            <strong>Portal roles</strong>
            <span>{{ roles().length }} available</span>
          </div>
          <span class="selected-count" *ngIf="selectedRoleId()">{{ rolePermissionCount(activeRole()) }} permissions</span>
        </div>

        <span class="search-box">
          <i class="pi pi-search"></i>
          <input pInputText type="search" [ngModel]="roleSearchText()" (ngModelChange)="roleSearchText.set($event)" placeholder="Search roles or permissions">
        </span>

        <div class="role-list">
          <button
            type="button"
            class="role-option"
            *ngFor="let role of filteredDialogRoles()"
            [class.selected]="isRoleSelected(role)"
            [class.previewed]="activeRoleId() === role.roleId"
            (click)="selectRole(role)"
            (mouseenter)="previewRole(role)">
            <span class="radio-dot" aria-hidden="true"></span>
            <span class="role-copy">
              <strong>{{ role.roleName }}</strong>
              <small>{{ role.description || 'Portal access role' }}</small>
            </span>
            <span class="permission-pill">{{ rolePermissionCount(role) }}</span>
          </button>

          <div class="empty-state compact" *ngIf="!filteredDialogRoles().length">
            No roles match the current search.
          </div>
        </div>
      </aside>

      <section class="permission-detail" *ngIf="activeRole() as role; else noRoleSelected">
        <div class="detail-header">
          <div>
            <span class="summary-kicker">Role details</span>
            <h3>{{ role.roleName }}</h3>
            <p>{{ role.description || 'Portal access role' }}</p>
          </div>
          <button
            pButton
            type="button"
            [label]="isRoleSelected(role) ? 'Selected' : 'Select role'"
            [icon]="isRoleSelected(role) ? 'pi pi-check' : 'pi pi-circle'"
            [class.p-button-outlined]="!isRoleSelected(role)"
            (click)="selectRole(role)">
          </button>
        </div>

        <div class="permission-summary">
          <span>{{ rolePermissionCount(role) }}</span>
          <strong>permissions included</strong>
        </div>

        <div class="permission-groups" *ngIf="activePermissionGroups().length; else noPermissions">
          <article class="permission-group" *ngFor="let group of activePermissionGroups()">
            <header>
              <strong>{{ group.name }}</strong>
              <span>{{ group.permissions.length }}</span>
            </header>
            <div class="permission-grid">
              <span class="permission-chip" *ngFor="let permission of group.permissions">{{ permission.permissionName }}</span>
            </div>
          </article>
        </div>

        <ng-template #noPermissions>
          <div class="empty-state compact">No permissions are attached to this role.</div>
        </ng-template>
      </section>

      <ng-template #noRoleSelected>
        <section class="permission-detail empty-detail">
          <div class="empty-state compact">Select a role to review its permissions.</div>
        </section>
      </ng-template>
    </div>
  </ng-template>

  <ng-template pTemplate="footer">
    <span class="dialog-footer-copy" *ngIf="selectedAccess() as access">
      Updating <strong>{{ access.customerName || 'this contact' }}</strong> will replace existing portal roles with the selected one.
    </span>
    <span class="dialog-footer-copy" *ngIf="!selectedAccess()">Select one role to continue.</span>
    <div class="dialog-footer-actions">
      <button pButton type="button" label="Cancel" class="p-button-text" (click)="hideRoleDialog()"></button>
      <button
        pButton
        type="button"
        label="Save role"
        icon="pi pi-save"
        (click)="saveRole()"
        [loading]="saving()"
        [disabled]="!selectedRoleId() || saving()">
      </button>
    </div>
  </ng-template>
</p-dialog>
